import { useContext, useRef, useState } from "react";
import { updateOccurrence } from "../api/EventAPI";
import AuthContext from "../context/AuthContext";
import { eventErrorMessage } from "../utils/eventErrors";
import {
  DraftValidationError,
  buildOccurrencePatch,
  occurrenceDraft,
  resetDraftField,
  setDraftField,
  setFirstDay,
  toggleAllDay,
} from "../utils/eventDrafts";

// "Edit this occurrence" (PATCH /event-occurrences/{id}/). Opens from the
// occurrence's effective values; untouched fields keep inheriting the series,
// "Use series …" makes a field inherit again (sent as null). Only changed
// fields are sent, so content-only edits never detach the schedule. No
// recurrence controls — those belong to the series.
//
// Hosted inside EventFocusModal; `onSaved(occurrence)` receives the backend's
// updated occurrence (same id and slot_date).
export default function OccurrenceEditForm({ event, occurrence, onSaved, onClose }) {
  const { token } = useContext(AuthContext);
  const [draft, setDraft] = useState(() => occurrenceDraft(event, occurrence));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous double-submit guard.
  const inFlight = useRef(false);

  const schedule = draft.schedule;
  const setSchedule = (next) => setDraft((d) => ({ ...d, schedule: next }));

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    setError(null);

    let patch;
    try {
      patch = buildOccurrencePatch(draft, event.timezone);
    } catch (err) {
      if (err instanceof DraftValidationError) {
        setError(err.message);
        return;
      }
      throw err;
    }
    if (Object.keys(patch).length === 0) {
      onClose?.();
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    try {
      const updated = await updateOccurrence(occurrence.id, patch, token);
      onSaved?.(updated);
    } catch (err) {
      console.error("Failed to update occurrence", err);
      // Not retried: the user decides what to change.
      setError(eventErrorMessage(err, "Couldn't save this occurrence. Please try again."));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const inputClass = "w-full border rounded px-3 py-2";

  const resetLink = (name, label) =>
    !draft[name].inherits && (
      <button
        type="button"
        onClick={() => setDraft((d) => resetDraftField(d, name))}
        className="mt-1 text-xs text-blue-600 hover:underline"
      >
        {label}
      </button>
    );

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white rounded-lg" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-lg font-bold mb-3">Edit Occurrence</h2>
      {error && (
        <div role="alert" className="mb-3 rounded bg-red-50 p-3 text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="occurrence-title" className="block text-sm mb-1">
            Title
          </label>
          <input
            id="occurrence-title"
            value={draft.title.value}
            onChange={(e) => setDraft((d) => setDraftField(d, "title", e.target.value))}
            className={inputClass}
          />
          {resetLink("title", "Use series title")}
        </div>

        <div>
          <label htmlFor="occurrence-location" className="block text-sm mb-1">
            Location
          </label>
          <input
            id="occurrence-location"
            value={draft.location.value}
            onChange={(e) => setDraft((d) => setDraftField(d, "location", e.target.value))}
            className={inputClass}
          />
          {resetLink("location", "Use series location")}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={schedule.isAllDay}
            onChange={(e) => setSchedule(toggleAllDay(schedule, e.target.checked, draft.originalSchedule))}
          />
          All day
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {schedule.isAllDay ? (
            <>
              <div>
                <label htmlFor="occurrence-first-day" className="block text-sm mb-1">
                  Starts on
                </label>
                <input
                  id="occurrence-first-day"
                  type="date"
                  value={schedule.firstDay}
                  onChange={(e) => setSchedule(setFirstDay(schedule, e.target.value))}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="occurrence-last-day" className="block text-sm mb-1">
                  Ends on
                </label>
                <input
                  id="occurrence-last-day"
                  type="date"
                  value={schedule.lastDay}
                  min={schedule.firstDay || undefined}
                  onChange={(e) => setSchedule({ ...schedule, lastDay: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="occurrence-starts" className="block text-sm mb-1">
                  Starts at
                </label>
                <input
                  id="occurrence-starts"
                  type="datetime-local"
                  value={schedule.startsLocal}
                  onChange={(e) => setSchedule({ ...schedule, startsLocal: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="occurrence-ends" className="block text-sm mb-1">
                  Ends at
                </label>
                <input
                  id="occurrence-ends"
                  type="datetime-local"
                  value={schedule.endsLocal}
                  onChange={(e) => setSchedule({ ...schedule, endsLocal: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
            </>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span id="occurrence-private-label" className="text-sm">
              Private Event
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={draft.is_private.value}
              aria-labelledby="occurrence-private-label"
              onClick={() => setDraft((d) => setDraftField(d, "is_private", !d.is_private.value))}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${
                draft.is_private.value ? "bg-black" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  draft.is_private.value ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          {resetLink("is_private", "Use series privacy")}
        </div>

        <div>
          <label htmlFor="occurrence-description" className="block text-sm mb-1">
            Description
          </label>
          <textarea
            id="occurrence-description"
            value={draft.description.value}
            onChange={(e) => setDraft((d) => setDraftField(d, "description", e.target.value))}
            className={inputClass}
            rows={3}
          />
          {resetLink("description", "Use series description")}
          <p className="text-xs text-gray-500 mt-1">
            Changes apply to this occurrence only. To change every occurrence, edit the entire series.
          </p>
        </div>

        <div className="pt-2 space-x-1">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Save"}
          </button>
          {onClose && (
            <button type="button" onClick={onClose} className="px-4 py-2 rounded border hover:bg-gray-50">
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
